class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.sectionsStorageKey = 'santo-cart-drawer-sections';
    this.handleOverlayClick = this.close.bind(this);
    this.handlePageShow = this.onPageShow.bind(this);
    this.handleHeaderCartIconClick = this.onHeaderCartIconClick.bind(this);
    this.handleHeaderCartIconKey = this.onHeaderCartIconKey.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayClick();
    this.bindHeaderCartIcon();
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    window.addEventListener('pageshow', this.handlePageShow);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.handlePageShow);
    this.unbindHeaderCartIcon();
    this.unbindOverlayClick();
  }

  bindOverlayClick() {
    this.unbindOverlayClick();
    this.overlayElement = this.querySelector('#CartDrawer-Overlay');
    if (this.overlayElement) {
      this.overlayElement.addEventListener('click', this.handleOverlayClick);
    }
  }

  unbindOverlayClick() {
    if (this.overlayElement) {
      this.overlayElement.removeEventListener('click', this.handleOverlayClick);
    }
  }

  bindHeaderCartIcon() {
    // Delegate at document level so the click handler survives even if a
    // third-party script (Klaviyo signup unit, etc.) re-renders the cart
    // icon and strips direct listeners. capture=true ensures we run before
    // any injected handlers that might stopPropagation.
    document.addEventListener('click', this.handleHeaderCartIconClick, true);
    document.addEventListener('keydown', this.handleHeaderCartIconKey, true);
  }

  unbindHeaderCartIcon() {
    document.removeEventListener('click', this.handleHeaderCartIconClick, true);
    document.removeEventListener('keydown', this.handleHeaderCartIconKey, true);
  }

  onHeaderCartIconClick(event) {
    const cartLink = event.target.closest('#cart-icon-bubble');
    if (!cartLink) return;
    event.preventDefault();
    this.open(cartLink);
  }

  onHeaderCartIconKey(event) {
    if (event.code.toUpperCase() !== 'SPACE') return;
    const cartLink = event.target.closest('#cart-icon-bubble');
    if (!cartLink) return;
    event.preventDefault();
    this.open(cartLink);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
  }

  onPageShow(event) {
    const navigationEntry =
      typeof performance.getEntriesByType === 'function'
        ? performance.getEntriesByType('navigation')[0]
        : null;
    const isBackForwardNavigation =
      event.persisted || navigationEntry?.type === 'back_forward';
    if (!isBackForwardNavigation) return;
    this.hydrateFromStoredSections();
    this.refreshContents();
  }

  readStoredSections() {
    try {
      const storedValue = window.sessionStorage.getItem(this.sectionsStorageKey);
      if (!storedValue) return null;
      return JSON.parse(storedValue);
    } catch (error) {
      return null;
    }
  }

  writeStoredSections(sections) {
    if (!sections) return;
    try {
      window.sessionStorage.setItem(
        this.sectionsStorageKey,
        JSON.stringify(sections),
      );
    } catch (error) {}
  }

  hydrateFromStoredSections() {
    const storedSections = this.readStoredSections();
    if (!storedSections) return;
    this.applySections(storedSections);
  }

  applySections(sections) {
    this.getSectionsToRender().forEach((section) => {
      const sectionMarkup = sections[section.id];
      if (!sectionMarkup) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        sectionMarkup,
        section.selector,
      );
    });

    const drawerSectionMarkup = sections['cart-drawer'];
    if (drawerSectionMarkup) {
      const drawerRoot = this.getSectionDOM(drawerSectionMarkup, 'cart-drawer');
      this.classList.toggle(
        'is-empty',
        Boolean(drawerRoot?.classList.contains('is-empty')),
      );
    }

    this.bindOverlayClick();
    this.setHeaderCartIconAccessibility();
  }

  refreshContents() {
    const sectionIds = this.getSectionsToRender().map((section) => section.id);
    const requestUrl = new URL(window.location.href);
    requestUrl.searchParams.set('sections', sectionIds.join(','));

    fetch(requestUrl.toString(), { cache: 'no-store' })
      .then((response) => response.json())
      .then((sections) => {
        this.writeStoredSections(sections);
        this.applySections(sections);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.writeStoredSections(parsedState.sections);
    this.applySections(parsedState.sections);

    setTimeout(() => {
      this.bindOverlayClick();
      this.setHeaderCartIconAccessibility();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
